import { useDisclosure } from "apps/commons/hooks/useDisclosure";
import { useOnboardingPageContextAtomValue } from "apps/oneOnboarding/onboardingPageContextAtom";
import { getOnboardingPropertyContextFromMap } from "apps/oneOnboarding/usePropertyContext";
import type { PropertyData } from "libs/codegen/property-onboarding";
import type { OnboardingPageContext } from "libs/codegen/property-onboarding/models/onboarding-page-context";
import { useEffect, useState } from "react";
import { type UseFieldArrayRemove, useFormContext } from "react-hook-form";
import type { BulkPropertyFormData } from "../BulkPropertyFormData";
import { getBulkOnboardSession, setBulkOnboardSession } from "../bulkOnboardSessionStorage";
import { useBulkOnboardingFormHandler } from "../useBulkOnboardingFormHandler";

export const usePropertyLicenseValidation = (remove: UseFieldArrayRemove) => {
    const { propertiesContext } = (useOnboardingPageContextAtomValue() ?? {}) as OnboardingPageContext;

    const {
        open: isLicenseRequiredModalOpen,
        onOpen: openLicenseRequiredModal,
        onClose: onCloseLicenseRequiredModal,
    } = useDisclosure();
    const {
        open: isLicensePartialModalOpen,
        onOpen: openLicensePartialModal,
        onClose: onCloseLicensePartialModal,
    } = useDisclosure();

    const {
        formState: { submitCount, errors },
        getValues,
    } = useFormContext<BulkPropertyFormData>();
    const { mutate } = useBulkOnboardingFormHandler();

    const [failedProperties, setFailedProperties] = useState<{ property: PropertyData; index: number }[]>([]);

    useEffect(() => {
        if (submitCount > 0 && errors.propertyDataList) {
            const properties = getValues("propertyDataList");
            const failed: { property: PropertyData; index: number }[] = [];

            let totalNeedsLicense = 0;
            properties.forEach((property) => {
                const licenseConfiguration = getOnboardingPropertyContextFromMap(
                    propertiesContext,
                    property.propertyId,
                    property.onboardingId,
                )?.licenseConfiguration;
                // TODO(PRO-841): Include requiredFileUpload when the file upload flow is implemented.
                const needsLicense =
                    licenseConfiguration?.requiredExemptible === true ||
                    licenseConfiguration?.requiredLicenseId === true;
                if (needsLicense) {
                    totalNeedsLicense++;
                }
            });

            if (Array.isArray(errors.propertyDataList)) {
                errors.propertyDataList.forEach((err, index) => {
                    if (!err) return;

                    const property = properties[index];
                    const hasLicenseError = !!err.licenseInformation;

                    if (hasLicenseError) {
                        failed.push({ property, index });
                    }
                });
            }

            if (failed.length > 0) {
                if (failed.length === totalNeedsLicense) {
                    openLicenseRequiredModal();
                } else {
                    setFailedProperties(failed);
                    openLicensePartialModal();
                }
            }
        }
    }, [submitCount, errors, propertiesContext, getValues, openLicenseRequiredModal, openLicensePartialModal]);

    const handleContinueAnyway = () => {
        onCloseLicensePartialModal();

        const onboardSession = getBulkOnboardSession();
        if (onboardSession !== null) {
            const failedIds = failedProperties.map((p) => p.property.propertyId.toString());
            const failedOnboardingIds = failedProperties
                .map((p) => p.property.onboardingId?.toString())
                .filter(Boolean);

            onboardSession.properties = onboardSession.properties.filter(
                (p) =>
                    !failedIds.includes(p.propertyId) &&
                    (!p.onboardingId || !failedOnboardingIds.includes(p.onboardingId)),
            );

            setBulkOnboardSession(onboardSession);
        }

        const indicesToRemove = failedProperties.map((p) => p.index).sort((a, b) => b - a);

        let hasOtherErrors = false;
        if (Object.keys(errors).some((key) => key !== "propertyDataList")) {
            hasOtherErrors = true;
        }
        if (Array.isArray(errors.propertyDataList)) {
            errors.propertyDataList.forEach((err, index) => {
                if (!err) return;
                if (indicesToRemove.includes(index)) return;

                if (Object.keys(err).length > 0) {
                    hasOtherErrors = true;
                }
            });
        }

        remove(indicesToRemove);

        if (!hasOtherErrors) {
            const currentData = getValues();
            const newPropertyDataList = currentData.propertyDataList.filter(
                (_, index) => !indicesToRemove.includes(index),
            );

            mutate({
                ...currentData,
                propertyDataList: newPropertyDataList,
            });
        }
    };

    return {
        isLicenseRequiredModalOpen,
        onCloseLicenseRequiredModal,
        isLicensePartialModalOpen,
        onCloseLicensePartialModal,
        failedProperties,
        handleContinueAnyway,
    };
};
